/**
 * Higher Order Component (HOC) for Widget Wrapper
 * Wraps widget components with unified top bar (title, drag handle, menu)
 * 
 * This is an alternative implementation to the children-based WidgetWrapper.
 * Can be reverted by using the original WidgetWrapper component.
 */

import React, { useRef, useEffect, useState, useMemo } from "react";
import { GripVertical } from "lucide-react";
import { WidgetOptionsMenu } from "./WidgetOptionsMenu";
import { DashboardWidget } from "../types";
import { Badge } from "../../ui/badge";
import type { WidgetComponentProps } from "../utils/widgetRegistry";
import { getWidgetTitle } from "../utils/widgetRegistry";

interface WidgetWrapperHOCProps extends WidgetComponentProps {
  widget: DashboardWidget;
  onHide: (instanceId: string) => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
  isDragging?: boolean;
}

/**
 * Higher Order Component that wraps a widget component with the wrapper functionality
 */
export function withWidgetWrapper<P extends WidgetComponentProps>(
  WidgetComponent: React.ComponentType<P>
) {
  return function WrappedWidget(props: P & WidgetWrapperHOCProps) {
    const {
      widget,
      onHide,
      onDragStart,
      onDragEnd,
      isDragging = false,
      ...widgetProps
    } = props;

    const widgetTitle = getWidgetTitle(widget.widgetId);
    const widgetRef = useRef<HTMLDivElement>(null);
    const [domHasSampleDataFlag, setDomHasSampleDataFlag] = useState(false);
    
    // Check for sample data badge in rendered DOM after React renders
    useEffect(() => {
      if (widgetRef.current) {
        // Look for badge with data-sample-data attribute anywhere in the widget
        const badge = widgetRef.current.querySelector('[data-sample-data="true"]');
        setDomHasSampleDataFlag(!!badge);
      }
    }, [widgetProps]);

    // Render the widget component
    const widgetContent = useMemo(() => {
      return <WidgetComponent {...(widgetProps as P)} />;
    }, [widgetProps, WidgetComponent]);

    return (
      <div className="h-full w-full relative group" ref={widgetRef}>
        <div className={`h-full w-full flex flex-col border rounded-lg bg-card shadow-sm ${isDragging ? 'opacity-50' : ''}`}>
          {/* Top bar with title, drag handle, and menu */}
          <div 
            className="flex flex-row items-center justify-between space-y-0 py-1.5 px-3 border-b group-hover:bg-muted/30 transition-colors cursor-grab active:cursor-grabbing"
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          >
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <GripVertical className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <span className="text-xs font-medium truncate">{widgetTitle}</span>
              {domHasSampleDataFlag && (
                <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                  <Badge variant="secondary" className="text-xs">
                    Sample Data
                  </Badge>
                </div>
              )}
            </div>
            <div 
              className="flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <WidgetOptionsMenu
                widget={widget}
                onHide={onHide}
              />
            </div>
          </div>
          
          {/* Widget content */}
          <div className="flex-1 flex items-end justify-start px-3 sm:px-4 md:px-5 py-2 sm:py-4 min-h-0 w-full overflow-hidden">
            <div className="w-full h-full flex items-end justify-start [&_.border]:border-0 [&_.rounded-lg]:rounded-none [&_.shadow-sm]:shadow-none [&_div]:max-w-full [&_svg]:max-w-full [&_div]:w-full [&_div]:flex-1 [&_.p-6]:p-0 [&_.pt-0]:pt-0">
              {widgetContent}
            </div>
          </div>
        </div>
      </div>
    );
  };
}
